import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Network } from '@capacitor/network';

// Shell natif iOS/Android : initialise les comportements qui n'existent qu'en
// app (barre de statut, splash, réseau, bouton retour Android, safe-areas).
// No-op total sur le web — la version web reste strictement inchangée.

let initialized = false;

function markNativeDom(): void {
  try {
    document.documentElement.classList.add('native-app');
    // Masque le widget Tawk.to (chat de conversion web) dans l'app, y compris
    // sur les routes sans PageLayout (onboarding, marketing redirigé, etc.).
    document.body.classList.add('anyhomes-user-logged');
    const hideTawk = () => {
      try {
        const tw = (window as any).Tawk_API;
        if (tw && typeof tw.hideWidget === 'function') tw.hideWidget();
      } catch {
        /* ignore */
      }
    };
    hideTawk();
    window.setInterval(hideTawk, 2000);
  } catch {
    /* ignore */
  }
}

function showOfflineBanner(): void {
  try {
    if (document.getElementById('native-offline-banner')) return;
    const el = document.createElement('div');
    el.id = 'native-offline-banner';
    el.textContent = 'Hors ligne';
    el.style.cssText = [
      'position:fixed',
      'left:0',
      'right:0',
      'top:var(--safe-top,env(safe-area-inset-top,0px))',
      'z-index:99999',
      'background:#DC2626',
      'color:#fff',
      'text-align:center',
      'font:600 13px -apple-system,sans-serif',
      'padding:6px 8px',
    ].join(';');
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  } catch {
    /* ignore */
  }
}

/** Android : `env(safe-area-inset-top)` vaut 0 dans la WebView (même en
 *  edge-to-edge) → le header passait sous la barre de statut. On récupère la
 *  hauteur réelle via le plugin StatusBar et on alimente `--safe-top`. */
async function applyAndroidStatusBarInset(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    const info = await StatusBar.getInfo();
    console.log('[native] status bar height =', info?.height);
    if (info?.height > 0) {
      document.documentElement.style.setProperty('--safe-top', `${info.height}px`);
    }
  } catch {
    /* ignore */
  }
}

export async function initNativeShell(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;
  markNativeDom();

  // Android : appliquer l'inset de la barre de statut EN PREMIER et de façon
  // indépendante. Sur Android 15+, `setBackgroundColor` peut échouer (edge-to-
  // edge) : il ne doit pas empêcher le calcul de `--safe-top`.
  if (Capacitor.getPlatform() === 'android') {
    void applyAndroidStatusBarInset();
    window.setTimeout(() => void applyAndroidStatusBarInset(), 300);
    window.setTimeout(() => void applyAndroidStatusBarInset(), 1200);
    window.addEventListener('resize', () => void applyAndroidStatusBarInset());
  }

  // Barre de statut (fond clair → icônes sombres).
  try {
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
    /* ignore */
  }
  if (Capacitor.getPlatform() === 'android') {
    try {
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    } catch {
      /* edge-to-edge Android 15+ : sans effet, géré par le CSS */
    }
  }

  // Réseau : bannière « Hors ligne ».
  try {
    const status = await Network.getStatus();
    if (!status.connected) showOfflineBanner();
    Network.addListener('networkStatusChange', (s) => {
      if (!s.connected) showOfflineBanner();
    });
  } catch {
    /* ignore */
  }

  // Bouton retour Android (no-op sur iOS).
  try {
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else App.exitApp();
    });
  } catch {
    /* ignore */
  }

  // Deep links : Universal Links (https://anyhomes.fr/...) et scheme
  // applicatif (anyhomes://route). Pour le scheme custom, la route peut être
  // dans le `host` (anyhomes://login → /login) ; on la recombine donc.
  const handleAppUrl = (rawUrl: string) => {
    try {
      const url = new URL(rawUrl);
      const isCustomScheme = url.protocol === 'anyhomes:';
      let path = isCustomScheme
        ? `/${url.host}${url.pathname}`.replace(/\/{2,}/g, '/')
        : url.pathname;
      path += `${url.search}${url.hash}`;
      if (!path || path === '/') return;
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {
      /* URL invalide */
    }
  };

  try {
    App.addListener('appUrlOpen', (event: { url: string }) => handleAppUrl(event.url));
    // Démarrage à froid via un lien : l'événement a pu être émis avant que le
    // listener ne soit posé, on récupère donc l'URL de lancement explicitement.
    App.getLaunchUrl()
      .then((res) => {
        if (res?.url) handleAppUrl(res.url);
      })
      .catch(() => {
        /* ignore */
      });
  } catch {
    /* ignore */
  }
}

/** Masque le splash natif (appelé après le premier rendu React pour éviter
 *  tout flash noir entre le splash et le contenu). */
export async function hideNativeSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await SplashScreen.hide();
  } catch {
    /* ignore */
  }
}
